import type { NuxtApp } from '#app'
import { createFetchError, type FetchContext } from 'ofetch'
import type { Logger } from '~/interfaces/Logger'
import { identityGeneration } from '~/utils/identityGeneration'
import { identityStartupInitialization } from '~/utils/identityStartupInitialization'
import { normalizeIdentity } from '~/utils/normalizeIdentity'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'

const redirectRejectedIdentity = async (
  nuxtApp: NuxtApp,
  config: ReturnType<typeof useSanctumConfig>
): Promise<void> => {
  const target = config.redirect.onAuthOnly
  const canRedirect = config.redirectIfUnauthenticated && typeof target === 'string'
  if (!canRedirect) return
  const router = await nuxtApp.runWithContext(() => useRouter())
  const hasCredentialAfterRouter = typeof sanctumTokenStorage.read() === 'string'
  if (hasCredentialAfterRouter) return
  const destination =
    config.redirect.keepRouteOnUnauthenticated === true
      ? { path: target, query: { redirect: router.currentRoute.value.fullPath.replace(/\/$/, '') } }
      : { path: target }
  await nuxtApp.callHook('sanctum:redirect', router.resolve(destination).fullPath)
  const hasCredential = typeof sanctumTokenStorage.read() === 'string'
  if (hasCredential) return
  await nuxtApp.runWithContext(() => navigateTo(destination))
}

export const refreshCurrentIdentity = async (
  nuxtApp: NuxtApp,
  logger: Logger,
  options: { initialize?: boolean } = {}
): Promise<boolean> => {
  const token = sanctumTokenStorage.read()
  const hasToken = typeof token === 'string'
  if (!hasToken) return false

  const { client, config, user } = await nuxtApp.runWithContext(() => ({
    client: useSanctumClient(),
    config: useSanctumConfig(),
    user: useSanctumUser()
  }))
  const endpoint = config.endpoints.user
  const hasEndpoint = typeof endpoint === 'string'
  if (!hasEndpoint) throw new Error('Sanctum identity endpoint is required')
  const initialIdentity = user.value
  const controller = new AbortController()
  let requestHeaders = new Headers({ Authorization: `Bearer ${token}` })
  let requestContext: FetchContext = { request: endpoint, options: { headers: requestHeaders } }
  const stopRequestObserver = nuxtApp.hook('sanctum:request', (_app, context) => {
    const belongsToRefresh = context.options.signal === controller.signal
    if (belongsToRefresh) {
      requestHeaders = context.options.headers
      requestContext = context
      const currentToken = sanctumTokenStorage.read()
      const hasCurrentCredential =
        typeof currentToken === 'string' &&
        requestHeaders.get('Authorization') === `Bearer ${currentToken}`
      if (!hasCurrentCredential) {
        controller.abort()
        controller.signal.throwIfAborted()
      }
    }
  })

  try {
    // Raw responses keep the vendor's 401 handler from resetting a replacement identity.
    const response = await client.raw(endpoint, {
      ignoreResponseError: true,
      signal: controller.signal
    })
    const currentToken = sanctumTokenStorage.read()
    const hasCurrentToken = typeof currentToken === 'string'
    const hasCurrentCredential =
      hasCurrentToken && requestHeaders.get('Authorization') === `Bearer ${currentToken}`
    const rejectedInitialIdentity =
      response.status === 401 &&
      !hasCurrentToken &&
      user.value === initialIdentity &&
      identityGeneration.hasRejectedCredential(requestContext)

    if (!hasCurrentCredential && !rejectedInitialIdentity) return false
    if (!response.ok) {
      const isRejectedCredential = response.status === 401
      if (isRejectedCredential) {
        user.value = null
        await redirectRejectedIdentity(nuxtApp, config)
      }
      const failureToken = sanctumTokenStorage.read()
      const hasFailureToken = typeof failureToken === 'string'
      const canReportFailure = hasFailureToken
        ? requestHeaders.get('Authorization') === `Bearer ${failureToken}`
        : isRejectedCredential &&
          user.value === null &&
          identityGeneration.hasRejectedCredential(requestContext)
      if (!canReportFailure) return false
      await nuxtApp.callHook('sanctum:error:response', requestContext)
      throw createFetchError(requestContext)
    }

    const identity = normalizeIdentity(response._data, logger)
    // The final credential check and state commit share one synchronous continuation.
    const canCommit = requestHeaders.get('Authorization') === `Bearer ${sanctumTokenStorage.read()}`
    if (!canCommit) return false
    user.value = identity
    await nuxtApp.callHook('sanctum:refresh')
    const shouldInitialize =
      options.initialize === true || identityStartupInitialization.isRequested(nuxtApp)
    if (!shouldInitialize) return true
    const canInitialize =
      requestHeaders.get('Authorization') === `Bearer ${sanctumTokenStorage.read()}`
    if (!canInitialize) return false
    identityStartupInitialization.consume(nuxtApp)
    await nuxtApp.callHook('sanctum:init')
    return true
  } catch (error: unknown) {
    if (controller.signal.aborted) return false
    const currentToken = sanctumTokenStorage.read()
    const hasCurrentToken = typeof currentToken === 'string'
    const hasCurrentCredential =
      hasCurrentToken && requestHeaders.get('Authorization') === `Bearer ${currentToken}`
    const hasRejectedCredential =
      !hasCurrentToken &&
      user.value === null &&
      identityGeneration.hasRejectedCredential(requestContext)
    if (!hasCurrentCredential && !hasRejectedCredential) return false
    throw error
  } finally {
    stopRequestObserver()
  }
}
